import { useCallback, useContext, useId, useMemo } from "react";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import SaveGuardPromptHandlersContext from "@coworker/apprestructured/src/save-guard/SaveGuardProvider/SaveGuardPromptHandlersContext";

/**
 * Wrap a function into a save guarded function.
 * @param fn the function to save guard
 * @param paramId the save guard id, uniquely generated if not provided
 */
function useSaveGuardedFunction(
  fn: () => void,
  paramId?: string
): {
  saveGuardedFunction: () => void;
  saveGuardIsEnabled: boolean;
  enableSaveGuard: () => void;
  disableSaveGuard: () => void;
} {
  const generatedId = useId();
  const id = paramId ?? generatedId;

  const { saveGuardIsEnabled, enableSaveGuard, disableSaveGuard } =
    useSaveGuard(id);

  const { setStayHandler, setLeaveHandler } = useContext(
    SaveGuardPromptHandlersContext
  );

  const saveGuardedFunction = useCallback(() => {
    if (saveGuardIsEnabled) {
      setStayHandler(() => {
        setStayHandler(null);
        setLeaveHandler(null);
      });
      setLeaveHandler(() => {
        disableSaveGuard();
        setStayHandler(null);
        setLeaveHandler(null);
        fn();
      });
    } else {
      fn();
    }
  }, [
    disableSaveGuard,
    fn,
    saveGuardIsEnabled,
    setStayHandler,
    setLeaveHandler,
  ]);

  return useMemo(() => {
    return {
      saveGuardedFunction,
      saveGuardIsEnabled,
      enableSaveGuard,
      disableSaveGuard,
    };
  }, [
    disableSaveGuard,
    enableSaveGuard,
    saveGuardIsEnabled,
    saveGuardedFunction,
  ]);
}

export default useSaveGuardedFunction;
